/**
 * 「既存地点から路線を作成する」機能 (docs/25 §10-6 拡張) 用の、点と線分/ポリラインの距離計算。
 *
 * 既存の一括解析バッチ (`roadCandidatePoints.ts`、functions側) は点と点のhaversine距離しか
 * 扱っておらず、点と線分の最短距離を計算する関数はadmin側にもfunctions側にも存在しない。
 * ここで新規に追加する。
 *
 * 対象となる線分は数十〜数百m程度の短い区間 (地図上で手描きする1本の道路沿いの線) のため、
 * 真の測地線距離ではなく、線分の中点付近を基準にした平面近似 (等角円筒図法、緯度による
 * 経度方向の縮尺補正込み) で十分な精度が出る。既存の `AreaReportPreviewMap.tsx` の
 * `haversineMeters` と同じ簡易実装方針を踏襲する。
 */
export interface LatLng {
  latitude: number;
  longitude: number;
}

const EARTH_RADIUS_M = 6371000;

export function haversineMeters(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/** 緯度経度を基準点周りのローカル平面座標 (m) に投影する (等角円筒図法、短距離前提の近似)。 */
function toLocalMeters(point: LatLng, origin: LatLng): { x: number; y: number } {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const x =
    toRad(point.longitude - origin.longitude) * Math.cos(toRad(origin.latitude)) * EARTH_RADIUS_M;
  const y = toRad(point.latitude - origin.latitude) * EARTH_RADIUS_M;
  return { x, y };
}

/** 点から線分への最短距離 (m)。線分の垂線の足が区間外なら、近い方の端点までの距離を返す。 */
export function distanceToSegmentM(point: LatLng, segStart: LatLng, segEnd: LatLng): number {
  const origin = segStart;
  const p = toLocalMeters(point, origin);
  const a = { x: 0, y: 0 };
  const b = toLocalMeters(segEnd, origin);

  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;

  if (lengthSq === 0) {
    return haversineMeters(point, segStart);
  }

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSq));
  const closest = { x: a.x + t * abx, y: a.y + t * aby };
  const dx = p.x - closest.x;
  const dy = p.y - closest.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** 点からポリライン全体 (連続する複数の線分) への最短距離 (m、全区間の最小値)。 */
export function distanceToPolylineM(point: LatLng, coords: LatLng[]): number {
  if (coords.length === 0) return Infinity;
  if (coords.length === 1) return haversineMeters(point, coords[0]!);
  let min = Infinity;
  for (let i = 0; i < coords.length - 1; i++) {
    const d = distanceToSegmentM(point, coords[i]!, coords[i + 1]!);
    if (d < min) min = d;
  }
  return min;
}

/** ポリラインの全長 (m、隣接区間のhaversine距離の合計)。 */
export function computePolylineLengthM(coords: LatLng[]): number {
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += haversineMeters(coords[i]!, coords[i + 1]!);
  }
  return total;
}
